import type { ComponentType } from 'react';
import { ListBulletIcon, ShareIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type { ViewMode } from './viewUrl';

const MODES: { mode: ViewMode; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { mode: 'graph', label: 'Graph', icon: ShareIcon },
  { mode: 'list', label: 'List', icon: ListBulletIcon },
];

type Props = {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
};

/**
 * Two joined buttons that draw the view as the canvas or as a tree; the pressed one is the current mode. Each mode
 * draws its own toolbar, so the page moves focus to the new one's pressed button (`data-view-toggle`).
 */
export function ViewModeToggle({ mode, onChange }: Props) {
  return (
    <div
      role="group"
      aria-label="Show the graph as"
      data-view-toggle
      className="flex rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:shadow-zinc-950/30 dark:ring-white/15"
    >
      {MODES.map(({ mode: value, label, icon: Icon }) => {
        const pressed = value === mode;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={pressed}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-1 text-xs/5 font-medium first:rounded-l-lg last:rounded-r-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              pressed
                ? 'bg-zinc-100 text-zinc-950 dark:bg-zinc-700 dark:text-white'
                : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-700/50 dark:hover:text-white',
            )}
            onClick={() => onChange(value)}
          >
            <Icon className="size-4 shrink-0" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
